import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import EditIcon from 'transfix-icons/build/EditIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { ICarrierTaskAutomationConfig } from 'types/carrier'
import { openContactInstructionsModal } from 'utils/actions/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { selectCarrierBulkEmailInformation } from 'utils/reducers/carrier'
import { IStore } from 'apps/carrier/utils/reducers'
import { Card, Toast, useToastState } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { InstructionsDialog, InstructionsDialogLegacy } from './InstructionsDialog'

interface IState {
  id?: string
  showDialog: boolean
  error?: string
  saving: boolean
  taskAutomationConfig?: ICarrierTaskAutomationConfig
  taskAutomationConfigError: boolean
}

export const Instructions = () => {
  const dispatch = useDispatch()
  const {
    error,
    id,
    saving,
    showDialog,
    taskAutomationConfig,
    taskAutomationConfigError,
  } = useSelector<IStore, IState>(({ carrier }) => ({
    id: carrier.carrierDetails?.id,
    showDialog: carrier.contactInstructionsModal,
    error: carrier.contactInstructionsError,
    saving: carrier.contactInstructionsSaving,
    taskAutomationConfig: carrier.carrierDetails?.taskAutomationConfig,
    taskAutomationConfigError: carrier.taskAutomationConfigError,
  }))

  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const { bulkEmailEnabled, carrierBulkEmailRecipients, carrierSpecificInfo } = useSelector(
    selectCarrierBulkEmailInformation
  )
  const openDialogHandler = () => dispatch(openContactInstructionsModal())
  const hasLoaded = !saving && !error

  useEffect(() => {
    if (taskAutomationConfigError) {
      openToast({
        toastType: 'alert',
        toastMessage:
          'An error occurred saving the task automation config. Please refresh and try again.',
      })
    }
  }, [taskAutomationConfigError])

  const isFmsAutomatedStatusUpdates = useFeatureFlag('fmsAutomatedStatusUpdates')

  return (
    <>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      <Card>
        <div className='row pv4'>
          <h3 className='fl mb0 mt2'>Contact Instructions</h3>
          <div className='fr'>
            <IconButton
              Icon={EditIcon}
              color='tertiary'
              onClick={openDialogHandler}
              data-test='edit-instructions-dialog'
              label='Edit'
              size='large'
            />
          </div>
        </div>
        {hasLoaded && (
          <div>
            <p className='mt2'>
              <span className='text-secondary mr2'>Daily Load Digest:</span>
              <span>{bulkEmailEnabled ? 'On' : 'Off'}</span>
            </p>
            {bulkEmailEnabled &&
              carrierBulkEmailRecipients.length > 0 &&
              carrierBulkEmailRecipients.map(recipient => (
                <p key={recipient.id}>
                  <span className='text-secondary mr2'>Recipient:</span>
                  {recipient.userFullName}
                </p>
              ))}
          </div>
        )}
        <hr className='bg-disabled' />
        <p className='text-secondary mb2'>Notes</p>
        {carrierSpecificInfo || DISPLAY.empty}
        {showDialog &&
          (isFmsAutomatedStatusUpdates ? (
            <InstructionsDialog
              instructionText={carrierSpecificInfo}
              bulkEmailEnabled={bulkEmailEnabled}
              carrierBulkEmailRecipients={carrierBulkEmailRecipients}
              error={error}
              saving={saving}
              id={id!}
              taskAutomationConfig={taskAutomationConfig}
            />
          ) : (
            <InstructionsDialogLegacy
              instructionText={carrierSpecificInfo}
              bulkEmailEnabled={bulkEmailEnabled}
              carrierBulkEmailRecipients={carrierBulkEmailRecipients}
              error={error}
              saving={saving}
              id={id!}
              taskAutomationConfig={taskAutomationConfig}
            />
          ))}
      </Card>
    </>
  )
}

export default Instructions
